import React, { useState, useEffect } from 'react';
import { requestNotificationPermission, initFCMForUser } from '../../services/fcmService';
import { useAuthBusiness } from '../../contexts/AuthBusinessContext';
import { BellAlertIcon, XMarkIcon } from '@heroicons/react/24/outline';

const NotificationPermissionBanner = () => {
    const { currentUser, userBusinesses, isAuthenticated } = useAuthBusiness();
    const [permissionState, setPermissionState] = useState('');
    const [isVisible, setIsVisible] = useState(false);
    const [isDismissed, setIsDismissed] = useState(() => {
        return localStorage.getItem('notification_prompt_dismissed') === 'true';
    });

    useEffect(() => {
        if (!isAuthenticated || !('Notification' in window)) return;

        // Electron ortamında (Masaüstü Agent) tarayıcı kilit ikonu vs. bulunmadığından bu banner'ı gösterme
        const isElectron = /electron/i.test(navigator.userAgent) || Boolean(window.process && window.process.type);
        if (isElectron) return;

        // Tarayıcıdaki mevcut izin durumunu kontrol et
        const currentPerm = Notification.permission;
        setPermissionState(currentPerm);

        // Zaten izin verilmişse veya kullanıcı daha önce 'Daha Sonra' demişse gösterme
        if (currentPerm === 'granted' || localStorage.getItem('notification_prompt_dismissed') === 'true') {
            setIsVisible(false);
            return;
        }

        // 'default' (henüz sorulmamış) veya 'denied' durumunda göster
        setIsVisible(true);
    }, [isAuthenticated]);

    const handleDismiss = () => {
        setIsDismissed(true);
        setIsVisible(false);
        // Kullanıcıyı sürekli boğmamak için kapatınca 7 gün veya kalıcı hatırla
        localStorage.setItem('notification_prompt_dismissed', 'true');
    };

    const handleEnableNotifications = async () => {
        try {
            const hasPermission = await requestNotificationPermission();
            const newPerm = Notification.permission || (hasPermission ? 'granted' : 'denied');
            setPermissionState(newPerm);
            setIsVisible(false);

            if (hasPermission) {
                localStorage.removeItem('notification_prompt_dismissed');
                const email = currentUser?.email || (JSON.parse(localStorage.getItem('user') || '{}'))?.email;
                const businessIds = (userBusinesses || []).map(b => 
                    typeof b === 'string' ? b : b.id
                ).filter(Boolean);
                
                if (email) {
                    initFCMForUser(email, businessIds).catch(err => {
                        console.warn('FCM token arka plan kaydı:', err?.message || err);
                    });
                }
            } else {
                // Tarayıcı izin vermediyse veya engelliyse kapatılsın, kullanıcı kilitlenmesin
                localStorage.setItem('notification_prompt_dismissed', 'true');
            }
        } catch (error) {
            console.error('Bildirim izni istenirken hata:', error);
            setIsVisible(false);
        }
    };

    if (!isVisible || isDismissed || permissionState === 'granted') {
        return null;
    }

    if (permissionState === 'denied') {
        return (
            <div className="fixed bottom-4 right-4 z-[9999] max-w-md bg-white rounded-2xl shadow-2xl border border-red-100 p-5 animate-fade-in">
                <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                        <BellAlertIcon className="h-5 w-5 text-red-600" aria-hidden="true" />
                    </div>
                    <div className="flex-1 text-left">
                        <h4 className="text-sm font-semibold text-gray-900">Bildirimler Tarayıcıda Engellenmiş</h4>
                        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                            Yeni siparişleri sesli duyabilmek için adres çubuğunun solundaki kilit (ayar) simgesine tıklayıp Bildirimlere izin verin.
                        </p>
                        <div className="mt-3 flex justify-end">
                            <button
                                type="button"
                                className="text-xs font-semibold text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                                onClick={handleDismiss}
                            >
                                Anladım, Kapat
                            </button>
                        </div>
                    </div>
                    <button onClick={handleDismiss} className="text-gray-400 hover:text-gray-600">
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden p-6 text-center space-y-4 relative">
                <button onClick={handleDismiss} className="absolute top-4 right-4 text-gray-400 hover:text-gray-500">
                    <XMarkIcon className="w-6 h-6" />
                </button>
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-100">
                    <BellAlertIcon className="h-8 w-8 text-blue-600 animate-bounce delay-150" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold leading-6 text-gray-900">Sipariş Bildirimleri</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                    Siparişleri cihazlarınıza <strong className="text-gray-700">anında ve sesli</strong> alabilmek için bildirim izni gereklidir. Lütfen tarayıcı izin penceresinde "İzin Ver"i seçin.
                </p>
                <div className="mt-6 flex flex-col gap-3">
                    <button
                        type="button"
                        className="inline-flex w-full justify-center items-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-base font-semibold text-white shadow-lg shadow-blue-500/30 hover:bg-blue-500 transition-all active:scale-95"
                        onClick={handleEnableNotifications}
                    >
                        Bildirimlere İzin Ver
                    </button>
                    <button
                        type="button"
                        className="inline-flex w-full justify-center rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 transition-colors"
                        onClick={handleDismiss}
                    >
                        Daha Sonra Hatırlat
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NotificationPermissionBanner;
